import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import DayPicker from 'react-day-picker-old';
import { Input } from '../../components-v2/Input';
import { Label } from '../../components-v2/Label';
import { TYPED_DATE_DISPLAY_FORMAT, parseTypedDate } from './parseTypedDate';
import { styleSheet } from './styles';

const VISIBLE_MONTH_FORMAT = 'YYYY-MM';

interface IProps {
	className?: string;
	startDateRequired?: boolean;
	endDateRequired?: boolean;
	from?: Date | null;
	hasEndDateError?: boolean;
	hasStartDateError?: boolean;
	maxDate?: Date;
	minDate?: Date;
	numberOfMonths?: number;
	onChange?(from: Date, to: Date): void;
	resetDates?: () => void;
	forceSmallText?: boolean;
	styles?: StyleDeclarationValue[];
	to?: Date | null;
	resetText?: string;
	disableScrollTo?: boolean;
	datePickerStyles?: StyleDeclarationValue[];
	additionalEndDateLabelText?: string;
	defaultOpen?: boolean;
	endDateTextLabel?: string;
}

export const CustomDateRange = ({
	className,
	startDateRequired = true,
	endDateRequired = false,
	from,
	hasEndDateError = false,
	hasStartDateError = false,
	maxDate,
	minDate,
	numberOfMonths = 1,
	onChange,
	resetDates,
	forceSmallText = false,
	styles = [],
	to,
	resetText = 'Reset dates',
	disableScrollTo = false,
	datePickerStyles = [],
	additionalEndDateLabelText = '',
	defaultOpen = true,
	endDateTextLabel = 'End Date',
}: IProps) => {
	// Allow the calendar to be hidden on initial load if hideCalendarOnInitialLoad is set to true else fallback to original behavior
	const [isShowingCalendar, setIsShowingCalendar] = useState(defaultOpen);
	const [visibleMonth, setVisibleMonth] = useState<Date>(from ?? new Date());
	const [startDraft, setStartDraft] = useState<string | null>(null);
	const [endDraft, setEndDraft] = useState<string | null>(null);
	const bottomDiv = React.useRef<HTMLDivElement>(null);
	const startInputId = React.useId();
	const endInputId = React.useId();

	const isSettingStartDate = React.useMemo(() => {
		return !from || from?.toLocaleDateString() !== to?.toLocaleDateString();
	}, [from, to]);
	const isSettingEndDate = React.useMemo(() => {
		return from && from?.toLocaleDateString() === to?.toLocaleDateString();
	}, [from, to]);

	const selectedMonthKey = from ? moment(from).format(VISIBLE_MONTH_FORMAT) : null;

	// Keyed on the month string rather than `from` itself: callers rebuild that Date on every
	// render, so depending on its identity would snap the calendar back mid-navigation.
	React.useEffect(() => {
		const targetKey = selectedMonthKey ?? moment().format(VISIBLE_MONTH_FORMAT);
		setVisibleMonth(current =>
			moment(current).format(VISIBLE_MONTH_FORMAT) === targetKey
				? current
				: moment(targetKey, VISIBLE_MONTH_FORMAT).toDate()
		);
	}, [selectedMonthKey]);

	const handleDayUpdate = (day: Date) => {
		const selection = moment(day);
		const disabledFromMinDates = minDate ? moment(minDate) : null;
		const disabledFromMaxDates = maxDate ? moment(maxDate) : null;
		const fromDate = from ? moment(from) : null;
		const toDate = to ? moment(to) : null;

		/**
		 * @If selected date is less than min date
		 * @OR
		 * @If selected date is greater than max date
		 * @return early and do nothing
		 */

		if (selection.isBefore(disabledFromMinDates, 'day') || selection.isAfter(disabledFromMaxDates, 'day')) {
			return;
		}
		if (
			(fromDate?.isAfter(selection, 'day') && toDate?.isSame(fromDate, 'day')) ||
			selection.isBefore(fromDate, 'day') ||
			toDate?.isAfter(fromDate, 'day')
		) {
			onChange?.(day, day);
			return;
		}

		const newStartDate = fromDate ? (toDate?.isBefore(fromDate, 'day') ? day : fromDate.toDate()) : day;
		const newEndDate = day;
		onChange?.(newStartDate, newEndDate);

		if (endDateRequired && newEndDate && moment(newEndDate).isAfter(newStartDate)) {
			setIsShowingCalendar(false);
		}
	};

	const handleResetClick = () => {
		resetDates?.();
	};

	const scrollToCalendar = () => {
		if (disableScrollTo) {
			return;
		}
		requestAnimationFrame(() => {
			bottomDiv.current?.scrollIntoView({ behavior: 'smooth' });
		});
	};

	const openCalendar = () => {
		if (isShowingCalendar) {
			return;
		}
		setIsShowingCalendar(true);
		// One thing we should come back and deal with in some way is when multiple calendars are open on the page it will scroll to the last one on the page.
		scrollToCalendar();
	};

	const isOutsideAllowedRange = (value: moment.Moment) => {
		return (!!minDate && value.isBefore(minDate, 'day')) || (!!maxDate && value.isAfter(maxDate, 'day'));
	};

	const commitStartDraft = () => {
		const parsed = startDraft === null ? null : parseTypedDate(startDraft);
		setStartDraft(null);
		if (!parsed || isOutsideAllowedRange(parsed)) {
			return;
		}
		const nextFrom = parsed.toDate();
		const nextTo = to && !moment(to).isBefore(nextFrom, 'day') ? to : nextFrom;
		setVisibleMonth(nextFrom);
		onChange?.(nextFrom, nextTo);
	};

	const commitEndDraft = () => {
		const parsed = endDraft === null ? null : parseTypedDate(endDraft);
		setEndDraft(null);
		if (!parsed || isOutsideAllowedRange(parsed)) {
			return;
		}
		const nextTo = parsed.toDate();
		const nextFrom = from && !moment(from).isAfter(nextTo, 'day') ? from : nextTo;
		setVisibleMonth(nextTo);
		onChange?.(nextFrom, nextTo);
	};

	const onDateKeyDown = (commit: () => void, abandon: () => void) => (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			commit();
			return;
		}
		if (e.key === 'Escape') {
			abandon();
		}
	};

	const startValue = startDraft ?? (from ? moment(from).format(TYPED_DATE_DISPLAY_FORMAT) : '');
	const endValue = endDraft ?? (!isSettingEndDate && to ? moment(to).format(TYPED_DATE_DISPLAY_FORMAT) : '');
	const smallTextStyle = endDateRequired || forceSmallText ? styleSheet.smallText : undefined;

	const modifiers = { end: to, start: from };
	const hasSelectedDateError = hasStartDateError || hasEndDateError;
	return (
		<div className={`${css(styleSheet.dateRangeWrapper, ...styles)} dateRangeWrapper`}>
			<header className={css(styleSheet.header, endDateRequired ? styleSheet.resetHidden : null)}>
				{!endDateRequired ? (
					<button className={css(styleSheet.reset)} onClick={handleResetClick} type='button'>
						{resetText}
					</button>
				) : null}

				<div className={css(styleSheet.dateWrapper)} onClick={openCalendar}>
					<Label
						htmlFor={startInputId}
						styles={[styleSheet.label, startDateRequired ? styleSheet.labelRequired : undefined]}
					>
						Start Date
					</Label>
					<Input
						aria-label='Start Date'
						id={startInputId}
						onBlur={commitStartDraft}
						onChange={e => setStartDraft(e.target.value)}
						onFocus={openCalendar}
						onKeyDown={onDateKeyDown(commitStartDraft, () => setStartDraft(null))}
						placeholder='Choose Date'
						styles={[
							styleSheet.dateInput,
							smallTextStyle,
							isShowingCalendar && isSettingStartDate ? styleSheet.selectionHighlight : undefined,
						]}
						value={startValue}
					/>
					{hasSelectedDateError ? (
						<p
							role='alert'
							className={css(styleSheet.formErrorLine, hasStartDateError ? styleSheet.formErrorLineVisible : null)}
						>
							Must select a start date
						</p>
					) : null}
				</div>

				<div className={css(styleSheet.dateWrapper)} onClick={openCalendar}>
					<Label
						htmlFor={endInputId}
						styles={[styleSheet.label, endDateRequired ? styleSheet.labelRequired : undefined]}
					>
						{endDateTextLabel}{' '}
						{endDateRequired ? null : <em className={css(styleSheet.placeholderText)}>{additionalEndDateLabelText}</em>}
					</Label>
					<Input
						aria-label={endDateTextLabel}
						id={endInputId}
						onBlur={commitEndDraft}
						onChange={e => setEndDraft(e.target.value)}
						onFocus={openCalendar}
						onKeyDown={onDateKeyDown(commitEndDraft, () => setEndDraft(null))}
						placeholder={endDateRequired ? 'Choose End Date' : 'Optional'}
						styles={[
							styleSheet.dateInput,
							smallTextStyle,
							isSettingEndDate ? styleSheet.selectionHighlight : undefined,
						]}
						value={endValue}
					/>

					{hasSelectedDateError ? (
						<p
							role='alert'
							className={css(styleSheet.formErrorLine, hasEndDateError ? styleSheet.formErrorLineVisible : null)}
						>
							Must select an end date
						</p>
					) : null}
				</div>
			</header>

			{isShowingCalendar ? (
				<DayPicker
					className={`Selectable  ${className ?? ''} ${css(styleSheet.customDateRange, datePickerStyles)}`}
					disabledDays={{
						after: maxDate ?? undefined,
						before: minDate ?? undefined,
					}}
					month={visibleMonth}
					numberOfMonths={numberOfMonths}
					onMonthChange={setVisibleMonth}
					selectedDays={[from, { from, to }]}
					modifiers={modifiers}
					onDayClick={handleDayUpdate}
				/>
			) : null}

			<div ref={bottomDiv} />
		</div>
	);
};
